import type { JSX } from 'preact';

/**
 * An engraved trailing vine along the foot of the hero, in place of the
 * coastal swell. Two strands — a fainter one set back — drawn as hairline
 * leaf outlines rather than filled shapes, which is what makes it read as
 * engraving rather than clip art.
 *
 * The SVG keeps its own aspect (width 100%, height auto) so the vine scales
 * with page width like a border rule, and strokes are non-scaling so the line
 * stays a hairline whether it is 390px wide or 1440.
 */

/** Deterministic pseudo-random, so the vine is identical on every render. */
function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) % 2147483648;
    return s / 2147483648;
  };
}

type Strand = {
  baseY: number;
  amp: number;
  period: number;
  spacing: number;
  leaf: number;
  opacity: number;
  seed: number;
};

const stemY = (x: number, s: Strand) => s.baseY + s.amp * Math.sin((x / s.period) * Math.PI * 2);
/** Tangent of the stem in degrees — leaves sit along the stem, not across it. */
const stemAngle = (x: number, s: Strand) =>
  (Math.atan((s.amp * ((Math.PI * 2) / s.period)) * Math.cos((x / s.period) * Math.PI * 2)) * 180) /
  Math.PI;

function stemPath(s: Strand) {
  let d = `M-40 ${stemY(-40, s).toFixed(1)}`;
  for (let x = -32; x <= 1240; x += 8) d += ` L${x} ${stemY(x, s).toFixed(1)}`;
  return d;
}

/** A lanceolate leaf: two mirrored quadratics from stem to tip. */
const leafPath = (len: number, wid: number) =>
  `M0 0 Q ${(len * 0.42).toFixed(1)} ${-wid.toFixed(1)} ${len.toFixed(1)} 0` +
  ` Q ${(len * 0.42).toFixed(1)} ${wid.toFixed(1)} 0 0`;

const STRANDS: Strand[] = [
  { baseY: 104, amp: 15, period: 232, spacing: 21, leaf: 12, opacity: 0.3, seed: 17 },
  { baseY: 86, amp: 23, period: 337, spacing: 30, leaf: 19, opacity: 0.62, seed: 53 },
];

function strand(s: Strand, key: string) {
  const rand = seeded(s.seed);
  const motionRand = seeded(s.seed + 1000);
  const leaves: JSX.Element[] = [];
  let side = 1;
  let n = 0;

  for (let x = -20; x < 1230; x += s.spacing) {
    const y = stemY(x, s);
    const base = stemAngle(x, s);
    const len = s.leaf * (0.82 + rand() * 0.4);
    const wid = len * 0.3;
    // Leaves spring from the stem at a shallow angle, alternating sides.
    const angle = base + side * (30 + rand() * 16);
    const sway = 1.5 + motionRand() * 2.5;
    const delay = 360 + ((x + 20) / 1250) * 1300 + motionRand() * 90;
    const motion = {
      '--vine-delay': `${delay.toFixed(0)}ms`,
      '--vine-duration': `${(2600 + motionRand() * 1800).toFixed(0)}ms`,
      '--vine-sway-from': `${(-sway * side).toFixed(2)}deg`,
      '--vine-sway-to': `${(sway * side).toFixed(2)}deg`,
    } as JSX.CSSProperties;

    leaves.push(
      <g
        key={`${key}-l${n}`}
        transform={`translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${angle.toFixed(1)})`}
      >
        <path
          class="hero__vine-leaf"
          d={leafPath(len, wid)}
          stroke="currentColor"
          stroke-width="1"
          vector-effect="non-scaling-stroke"
          fill="none"
          style={motion}
        />
      </g>,
    );

    side *= -1;
    n++;
  }

  return (
    <g key={key} class={`hero__vine-strand hero__vine-strand--${key}`} opacity={s.opacity}>
      <path
        class="hero__vine-stem"
        d={stemPath(s)}
        stroke="currentColor"
        stroke-width="1"
        vector-effect="non-scaling-stroke"
        clip-path="url(#hero-vine-reveal)"
        fill="none"
      />
      {leaves}
    </g>
  );
}

export function Vine() {
  return (
    <svg class="hero__vine" viewBox="0 42 1200 96" fill="none" aria-hidden="true">
      <defs>
        <clipPath id="hero-vine-reveal" clipPathUnits="userSpaceOnUse">
          <rect class="hero__vine-reveal" x="-50" y="0" width="1300" height="160" />
        </clipPath>
      </defs>
      {STRANDS.map((s, i) => strand(s, `s${i}`))}
    </svg>
  );
}
